import { useCallback, useEffect, useState } from 'react';
import {
  NamiEntitlement,
  NamiEntitlementManager,
} from '@namiml/web-sdk';
import ErrorMessage from '../components/ErrorMessage';

const EntitlementsPage = () => {
  const [entitlements, setEntitlements] = useState<NamiEntitlement[]>([]);
  const [error, setError] = useState('');

  const loadEntitlements = useCallback(() => {
    try {
      const active = NamiEntitlementManager.active();
      setEntitlements(active);
      return active;
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to fetch entitlements: ${details}`);
      return [];
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      await NamiEntitlementManager.refresh();
      loadEntitlements();
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to refresh entitlements: ${details}`);
    }
  }, [loadEntitlements]);

  const handleClear = useCallback(() => {
    try {
      NamiEntitlementManager.clearProvisionalEntitlementGrants();
      loadEntitlements();
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to clear entitlements: ${details}`);
    }
  }, [loadEntitlements]);

  const inspectEntitlement = useCallback((referenceId: string) => {
    try {
      const active = NamiEntitlementManager.isEntitlementActive(referenceId);
      console.log(`Entitlement ${referenceId} active:`, active);
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to inspect entitlement: ${details}`);
    }
  }, []);

  useEffect(() => {
    loadEntitlements();
    const unsubscribe = NamiEntitlementManager.registerActiveEntitlementsHandler((active) => {
      setEntitlements(active);
    });
    return () => unsubscribe?.();
  }, [loadEntitlements]);

  return (
    <div className="screen-root">
      <div className="panel stack">
        <h1 className="panel-title">Entitlements</h1>
        <p className="panel-subtitle">Check entitlement status and simulate updates.</p>
        {error && <ErrorMessage message={error} />}
        <div className="inline-actions">
          <button type="button" className="tv-button" onClick={handleRefresh}>
            Refresh
          </button>
          <button type="button" className="tv-button" data-variant="ghost" onClick={handleClear}>
            Clear Provisional
          </button>
        </div>

        <div className="panel">
          <h2 className="panel-title">Active Entitlements</h2>
          <div className="stack">
            {entitlements.length === 0 && (
              <div className="panel-subtitle">No active entitlements.</div>
            )}
            {entitlements.map((entitlement) => (
              <button
                key={entitlement.referenceId}
                type="button"
                className="tv-button"
                onClick={() => inspectEntitlement(entitlement.referenceId)}
              >
                {entitlement.referenceId}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntitlementsPage;
